'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { usePrivateMode } from '@/lib/privateMode';

interface SearchPromptsButtonProps {
  /** Text to look for in image prompts. */
  text: string;
  /** Overrides the default corner-overlay styling. */
  className?: string;
  title?: string;
  /** Called on click — modals pass their close handler so results aren't left covered. */
  onNavigate?: () => void;
  /**
   * Resolved at click time instead of render time. Use when the text depends on transient
   * state a static href can't capture, such as the user's current text selection.
   */
  resolveText?: () => string;
}

const OVERLAY =
  'p-1.5 rounded-lg bg-black/60 text-white/80 hover:bg-[var(--accent)] hover:text-black transition-all cursor-pointer';

/**
 * Jumps to the private prompt search pre-filled with `text`, so any list item or image
 * can be traced through the whole library. Renders nothing unless private mode is
 * unlocked — /search is private, and a link to it would only dead-end.
 */
export default function SearchPromptsButton({
  text,
  className,
  title,
  onNavigate,
  resolveText,
}: SearchPromptsButtonProps) {
  const privateEnabled = usePrivateMode((s) => s.enabled);
  const router = useRouter();
  const trimmed = text?.trim() ?? '';
  if (!privateEnabled || !trimmed) return null;

  return (
    <Link
      href={`/search?q=${encodeURIComponent(trimmed)}`}
      onClick={(e) => {
        e.stopPropagation();
        // A click-time value can differ from the href, so navigate by hand in that case
        const resolved = resolveText?.().trim();
        if (resolved && resolved !== trimmed) {
          e.preventDefault();
          router.push(`/search?q=${encodeURIComponent(resolved)}`);
        }
        onNavigate?.();
      }}
      className={className ?? OVERLAY}
      title={title ?? 'Find images with this in the prompt'}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </Link>
  );
}
